import { createSlice } from '@reduxjs/toolkit';
import { parseRoles, parseToken } from '@utils/permissions';
import { loadSetting, removeSetting, saveSetting } from '@utils/localStorage';

import type { CognitoParsedToken } from '@/types/cognito';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RoleStore } from '@/types/permissions';

export interface AuthState {
    IdToken: string | null;
    TokenPayload: CognitoParsedToken | null;
    Roles: RoleStore;
    RefreshToken: string | null;
    Persist: boolean;
}

const initialState: AuthState = {
    IdToken: loadSetting('IdToken') ?? null,
    TokenPayload: parseToken(loadSetting('IdToken')) ?? null,
    Roles: parseRoles(parseToken(loadSetting('IdToken')) ?? null),
    RefreshToken: loadSetting('RefreshToken') ?? null,
    Persist: loadSetting('Persist') ?? false,
};

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setIdToken: (state: AuthState, action: PayloadAction<string>) => {
            saveSetting('IdToken', action.payload);
            state.IdToken = action.payload;
            state.TokenPayload = parseToken(action.payload);
            state.Roles = parseRoles(state.TokenPayload);
        },
        setRefreshToken: (state: AuthState, action: PayloadAction<string>) => {
            saveSetting('RefreshToken', action.payload);
            state.RefreshToken = action.payload;
        },
        setPersist: (state: AuthState, action: PayloadAction<boolean>) => {
            saveSetting('Persist', action.payload);
            state.Persist = action.payload;
        },
        logout: (state: AuthState) => {
            // Targeted removes — localStorage.clear() also nukes user prefs (columns, tz, date range).
            removeSetting('IdToken');
            removeSetting('RefreshToken');
            removeSetting('Persist');
            state.IdToken = '';
            state.RefreshToken = '';
            state.Roles = parseRoles(null);
            state.Persist = false;
        },
    },
});

export const { setIdToken, setRefreshToken, setPersist, logout } = authSlice.actions;
export default authSlice.reducer;
